import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Package, ImagePlus, X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";
import { money } from "@/components/StatusBadge";
import { itemShare, coopTotals, SPLIT_TYPES, emptyCoopItem } from "@/lib/coop";

// Dynamic line-item editor for a Bulk Buying Co-op deal.
// items: array of { name, unit_price, split_type, total_units, partner_units, partner_pct, partner_amount }
export default function CoopItemsEditor({ items = [], onChange, idPrefix = "coop" }) {
  const rows = items.length ? items : [emptyCoopItem()];
  const [suggesting, setSuggesting] = useState(-1);

  const update = (idx, key, value) => {
    const next = rows.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    onChange(next);
  };
  const addRow = () => onChange([...rows, emptyCoopItem()]);
  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length ? next : [emptyCoopItem()]);
  };

  const onThumbFile = async (idx, file) => {
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      update(idx, "image_url", dataUrl);
    } catch {
      toast.error("Could not read image");
    }
  };

  const suggestPhoto = async (idx) => {
    const name = String(rows[idx]?.name || "").trim();
    if (!name) {
      toast.error("Type the product name first");
      return;
    }
    setSuggesting(idx);
    try {
      const { data } = await api.post("/deals/coop-item-image", { name });
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      update(idx, "image_url", BACKEND + data.image_path);
      toast.success("Photo suggested — swap it anytime");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate a photo");
    } finally {
      setSuggesting(-1);
    }
  };

  const totals = coopTotals(rows);

  return (
    <div data-testid={`${idPrefix}-items-editor`} className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-900">
        <Package className="h-4 w-4" /> Co-op basket — add each product to share
      </div>
      <p className="mt-0.5 text-[11px] text-blue-800">
        Add every item in the pack. For each, set the unit price and how much of it the partner takes.
      </p>

      <div className="mt-3 space-y-3">
        {rows.map((it, idx) => {
          const split = String(it.split_type || "qty");
          const share = itemShare(it);
          return (
            <div key={idx} data-testid={`${idPrefix}-item-row-${idx}`} className="rounded-md border border-black/10 bg-white p-3">
              <div className="flex items-start gap-2">
                <div>
                  <Label className="text-xs">Photo</Label>
                  <input
                    type="file" accept="image/*" className="hidden"
                    id={`${idPrefix}-item-thumb-file-${idx}`}
                    data-testid={`${idPrefix}-item-thumb-file-${idx}`}
                    onChange={(e) => onThumbFile(idx, e.target.files?.[0])}
                  />
                  <div className="relative">
                    <button
                      type="button"
                      data-testid={`${idPrefix}-item-thumb-${idx}`}
                      onClick={() => document.getElementById(`${idPrefix}-item-thumb-file-${idx}`).click()}
                      className="mt-1 flex h-11 w-11 items-center justify-center overflow-hidden rounded-md border border-dashed border-black/20 bg-white text-muted-foreground hover:border-primary/50"
                      aria-label="Add product photo"
                    >
                      {it.image_url
                        ? <img src={it.image_url} alt="" className="h-full w-full object-cover" />
                        : <ImagePlus className="h-4 w-4" />}
                    </button>
                    {it.image_url && (
                      <button
                        type="button"
                        data-testid={`${idPrefix}-item-thumb-clear-${idx}`}
                        onClick={() => update(idx, "image_url", "")}
                        className="absolute -right-1.5 -top-0.5 rounded-full bg-white text-muted-foreground shadow hover:text-destructive"
                        aria-label="Remove photo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={`${idPrefix}-item-suggest-${idx}`}
                    onClick={() => suggestPhoto(idx)}
                    disabled={suggesting === idx}
                    className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    {suggesting === idx
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> AI…</>
                      : <><Sparkles className="h-3 w-3" /> Suggest</>}
                  </button>
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Product name</Label>
                  <Input
                    data-testid={`${idPrefix}-item-name-${idx}`}
                    value={it.name || ""}
                    onChange={(e) => update(idx, "name", e.target.value)}
                    placeholder="e.g. Rotisserie Chicken 2-pack"
                  />
                </div>
                <button
                  type="button"
                  data-testid={`${idPrefix}-item-remove-${idx}`}
                  onClick={() => removeRow(idx)}
                  className="mt-6 text-muted-foreground hover:text-destructive"
                  aria-label="Remove item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Unit price ($)</Label>
                  <Input
                    data-testid={`${idPrefix}-item-price-${idx}`}
                    type="number" min="0" step="0.01"
                    value={it.unit_price ?? ""}
                    onChange={(e) => update(idx, "unit_price", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Partner takes</Label>
                  <select
                    data-testid={`${idPrefix}-item-split-${idx}`}
                    value={split}
                    onChange={(e) => update(idx, "split_type", e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {SPLIT_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                {split === "qty" && (
                  <>
                    <div>
                      <Label className="text-xs">Total units</Label>
                      <Input
                        data-testid={`${idPrefix}-item-total-units-${idx}`}
                        type="number" min="0" step="1"
                        value={it.total_units ?? ""}
                        onChange={(e) => update(idx, "total_units", e.target.value)}
                        placeholder="e.g. 6"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Partner's units</Label>
                      <Input
                        data-testid={`${idPrefix}-item-partner-units-${idx}`}
                        type="number" min="0" step="1"
                        value={it.partner_units ?? ""}
                        onChange={(e) => update(idx, "partner_units", e.target.value)}
                        placeholder="e.g. 2"
                      />
                    </div>
                  </>
                )}
                {split === "pct" && (
                  <div>
                    <Label className="text-xs">Partner's share (%)</Label>
                    <Input
                      data-testid={`${idPrefix}-item-partner-pct-${idx}`}
                      type="number" min="0" max="100" step="1"
                      value={it.partner_pct ?? ""}
                      onChange={(e) => update(idx, "partner_pct", e.target.value)}
                      placeholder="e.g. 50"
                    />
                  </div>
                )}
                {split === "amount" && (
                  <div>
                    <Label className="text-xs">Partner pays ($)</Label>
                    <Input
                      data-testid={`${idPrefix}-item-partner-amount-${idx}`}
                      type="number" min="0" step="0.01"
                      value={it.partner_amount ?? ""}
                      onChange={(e) => update(idx, "partner_amount", e.target.value)}
                      placeholder="e.g. 8.00"
                    />
                  </div>
                )}
                <div className="flex items-end justify-end">
                  <span data-testid={`${idPrefix}-item-share-${idx}`} className="text-xs text-muted-foreground">
                    Partner's share: <b className="text-foreground">{money(share)}</b>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button" variant="outline" size="sm"
        data-testid={`${idPrefix}-add-item-btn`}
        onClick={addRow}
        className="mt-3 rounded-full border-blue-300 text-blue-800"
      >
        <Plus className="mr-1 h-4 w-4" /> Add another product
      </Button>

      <div className="mt-3 flex items-center justify-between rounded-md bg-white p-2.5 text-sm">
        <span className="text-muted-foreground">Full basket price</span>
        <span data-testid={`${idPrefix}-basket-total`} className="font-semibold">{money(totals.priceTotal)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between rounded-md bg-white p-2.5 text-sm">
        <span className="text-muted-foreground">Partner's total share</span>
        <span data-testid={`${idPrefix}-share-total`} className="font-semibold text-accent">{money(totals.shareTotal)}</span>
      </div>
    </div>
  );
}
